// Display metadata for the bank behind an IBAN -- its name and logo,
// keyed by the four-character bank code carried in the IBAN itself
// (see bankCodeFromIban in raastQr.js, which extracts it).
//
// This is the same layering the platform files already use: structure
// and validity live in the shared module (raastQr.js knows where the
// bank code sits inside an IBAN, exactly as socialPlatformRegistry.js
// knows which platforms exist), and what it *looks like* lives in a
// display-only table on top (this file, as socialPlatforms.js is for
// platforms). Nothing here is consulted to decide whether an IBAN is
// acceptable -- an unlisted bank's IBAN is just as valid as a listed
// one, it simply renders without a name.
//
// WHY THIS IS DERIVED RATHER THAN CHOSEN
//
// The bank is read from the IBAN instead of being a separate "HBL IBAN"
// / "Meezan IBAN" option in the platform dropdown. A per-bank entry type
// would let someone pick "HBL IBAN" and paste a Meezan account, and the
// profile would then caption a payment detail with the wrong bank --
// a label the payer has every reason to trust and no way to check. The
// IBAN cannot disagree with itself, so deriving it is the only version
// that cannot be wrong. socialPlatformRegistry.js keeps one 'iban' key
// for that reason and this file is what makes that sufficient.
//
// CommonJS rather than an ES module, and subject to the same constraint
// as every other CommonJS file under client/src: no for...of, no
// iterable spread, no async/generators, because Babel down-levelling any
// of those injects an `import` helper that makes webpack treat the
// compiled output as an ES module and freeze module.exports. See the
// header of raastQr.js for the full account, and
// test/sharedCjsModules.test.js, which enforces it. The reason to accept
// that constraint here is that it makes the table require()-able from
// the root Jest suite, which has no Babel transform and so cannot load
// an ES module at all.
//
// ADDING A BANK
//
// One row below, one logo file at public/images/<icon>. Nothing else --
// no schema change, no migration, and existing IBANs at that bank start
// showing the name the moment the row lands.
//
// A code that is not listed falls back to no name and the generic IBAN
// icon, which is the point: an unlisted bank still works, it is just
// unadorned. That fallback is what makes a partial table safe to ship,
// so a bank belongs here only when its code has actually been confirmed
// -- a wrong row is worse than a missing one, because a missing row
// shows nothing while a wrong row shows a different bank's name.
//
// The codes are the first four characters of each bank's SWIFT/BIC,
// which is what Pakistani IBANs embed (HABBPKKA -> HABB).

const { bankCodeFromIban } = require('./raastQr');

const BANK_BY_CODE = {
  HABB: { name: 'HBL', icon: '/images/bank-habb.png' },
  MEZN: { name: 'Meezan Bank', icon: '/images/bank-mezn.png' },
  UNIL: { name: 'UBL', icon: '/images/bank-unil.png' },
  MUCB: { name: 'MCB Bank', icon: '/images/bank-mucb.png' },
  ABPA: { name: 'Allied Bank', icon: '/images/bank-abpa.png' },
  ALFH: { name: 'Bank Alfalah', icon: '/images/bank-alfh.png' },
  FAYS: { name: 'Faysal Bank', icon: '/images/bank-fays.png' },
  BAHL: { name: 'Bank Al Habib', icon: '/images/bank-bahl.png' },
  NBPA: { name: 'National Bank of Pakistan', icon: '/images/bank-nbpa.png' },
  SCBL: { name: 'Standard Chartered', icon: '/images/bank-scbl.png' },
  DUIB: { name: 'Dubai Islamic Bank', icon: '/images/bank-duib.png' },
  HMBL: { name: 'Habib Metropolitan Bank', icon: '/images/bank-hmbl.png' },
  ASCM: { name: 'Askari Bank', icon: '/images/bank-ascm.png' },
  SONE: { name: 'Soneri Bank', icon: '/images/bank-sone.png' },
  JSBL: { name: 'JS Bank', icon: '/images/bank-jsbl.png' },
  BPUN: { name: 'Bank of Punjab', icon: '/images/bank-bpun.png' },
  SDPY: { name: 'SadaPay', icon: '/images/bank-sdpy.png' },
};

// Returns { code, name, icon } for a recognised bank, or null.
//
// null covers three different situations on purpose -- the value is not
// structurally an IBAN, or it is but the code is not in the table, or
// there is no value at all -- because every caller does the same thing
// in all three: show the generic IBAN icon and no bank name.
const bankFromIban = (iban) => {
  const code = bankCodeFromIban(iban);
  if (!code) return null;
  const bank = BANK_BY_CODE[code];
  if (!bank) return null;
  return { code: code, name: bank.name, icon: bank.icon };
};

// The bank's display name alone, or '' -- mirrors platformLabel/
// platformNote in socialPlatforms.js, so a caller that only wants text
// can render it unconditionally.
const bankNameFromIban = (iban) => {
  const bank = bankFromIban(iban);
  return bank ? bank.name : '';
};

module.exports = {
  BANK_BY_CODE,
  bankFromIban,
  bankNameFromIban,
};
